import axios from 'axios';

// Define base URL with fallbacks
const BASE_URL = process.env.REACT_APP_API_URL || 
                 'https://server-rejection-tales.fly.dev' || 
                 'http://localhost:4000';
                 
// Create API instance without timeout
export const API = axios.create({ 
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    timeout: 0,  // 0 means no timeout
    withCredentials: true
});

// Add retry interceptor
API.interceptors.response.use(undefined, async (err) => {
    const { config, message } = err;
    if (!config || !config.retry) {
        return Promise.reject(err);
    }
    
    // Set retry count
    config.retryCount = config.retryCount || 0;
    
    if (config.retryCount >= 3) {
        return Promise.reject(err);
    }
    
    // Increase retry count
    config.retryCount += 1;
    
    // Create new promise to handle retry
    const backoff = new Promise(resolve => {
        setTimeout(() => resolve(), 1000 * config.retryCount);
    });
    
    await backoff;
    return API(config);
});

// Add request interceptor with better token handling and logging
API.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Add CORS headers for PATCH requests
        if (config.method === 'patch') {
            config.headers['Access-Control-Request-Method'] = 'PATCH';
        }
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for better error handling
API.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            // Redirect to tales page instead of auth
            window.location.href = '/tales';
        }
        return Promise.reject(error);
    }
);

// Add this interceptor to include the token in requests
API.interceptors.request.use((req) => {
    const token = localStorage.getItem('token');
    if (token) {
        req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
});

// API functions with better error handling
export const saveChat = (messages, chatId = null) => {
    const token = localStorage.getItem('token');
    
    return API.post('/chats', { messages, chatId })
        .then(response => {
            return response.data;
        })
        .catch(error => {
            console.error('Error saving chat:', error.response?.data || error.message);
            throw error;
        });
};

export const fetchUserChats = () => {
    const token = localStorage.getItem('token');
    
    return API.get('/chats')
        .then(response => {
            return response.data;
        })
        .catch(error => {
            console.error('Error fetching chats:', error.response?.data || error.message);
            throw error;
        });
};

// Auth token management
export const setAuthToken = (token) => {
    if (token) {
        API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
};

export const removeAuthToken = () => {
    delete API.defaults.headers.common['Authorization'];
};

// Auth endpoints
export const signIn = (formData) => API.post('/auth/signin', formData);
export const signUp = (formData) => API.post('/users/signup', formData);
export const updateProfile = (formData) => API.patch('/users/update', formData);

// Set up interceptor to handle token on initial load
const token = localStorage.getItem('token');
if (token) {
    setAuthToken(token);
}

// Tales endpoints
export const fetchTales = () => API.get('/tales');
export const fetchTale = (id) => API.get(`/tales/${id}`);
export const createTale = (newTale) => {
    return API.post('/tales', newTale)
        .catch(error => {
            throw error;
        });
};

// Comments endpoints
export const fetchComments = (id) => API.get(`/tales/${id}/comments`);
export const createComment = (taleId, commentData) => {
    
    return API.post(`/tales/${taleId}/comments`, commentData)
        .then(response => {
            return response;
        })
        .catch(error => {
            throw error;
        });
};

// Groups endpoints
export const fetchGroups = () => API.get('/groups');

// Support Assistant endpoints
export const fetchChatData = (searchQuery) => {
    return API.get(`/supportAssistant/chat`, {
        params: { 
            searchQuery: JSON.stringify(searchQuery)
        },
        timeout: 0  // No timeout for chat requests
    })
    .then(response => {
        return response;
    })
    .catch(error => {
        console.error('Chat request error:', error);
        throw error;
    });
};

export const fetchSearchedInsiderTrans = (searchQuery) => API.get(`/insiderTrans/search?searchQuery=${JSON.stringify(searchQuery)}`);
export const fetchTopPriceData = () => API.get('/dailyPrices');
export const fetchSearchedPrices = (searchQuery) => API.get(`/dailyPrices/search?searchQuery=${searchQuery.searchQuery || 'none'}`);
export const fetchEquityHoldings = () => API.get('/equityHoldings');
export const fetcheSearchedEquityHoldings = (searchQuery) => API.get(`/equityHoldings/search?searchQuery=${JSON.stringify(searchQuery)}`);
export const fetchStockData = () => API.get('/dailyStocks');
export const fetchSearchedStockData = (searchQuery) => API.get(`/dailyStocks/search?searchQuery=${JSON.stringify(searchQuery)}`);

// Add this with your other API functions
export const deleteChat = async (chatId, token) => {
    try {
        const response = await axios.delete(`${process.env.REACT_APP_API_URL}/chats/${chatId}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting chat:', error);
        throw error;
    }
};

// Update user function
export const updateUser = (id, updatedData) => {
    return API.patch(`/users/update`, updatedData, {
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Request-Method': 'PATCH'
        }
    });
};

export const deleteTale = (id) => API.delete(`/tales/${id}`);

export const updateTale = (id, updatedTale) => API.patch(`/tales/${id}`, updatedTale);

export const searchTales = (params) => API.get(`/tales/search?${params}`);

export const updateComment = (id, updatedComment) => API.patch(`/tales/comments/${id}`, updatedComment);
export const deleteComment = (id) => API.delete(`/tales/comments/${id}`);

export const fetchResources = () => API.get('/resources');
export const fetchResourcesByGroup = (groupId) => API.get(`/resources/group/${groupId}`);
export const fetchResourceCategories = () => API.get('/resources/categories');
export const createResource = (newResource) => API.post('/resources', newResource);

// Feedback API functions
export const submitFeedback = (feedbackData) => API.post('/feedback/submit', feedbackData);
export const fetchFeedbacks = () => API.get('/feedback');
export const fetchFeedbackById = (id) => API.get(`/feedback/${id}`);
export const updateFeedbackStatus = (id, status) => API.patch(`/feedback/${id}/status`, { status });
export const deleteFeedback = (id) => API.delete(`/feedback/${id}`);

export default API;
